// NIKA (owner, 2026-08-13) — **THE FOUNDATIONAL REPORT CHIP, AND IT MOVED FOR R8's OWN STATED
// CURRENCY: THE THING THAT HAD TO MOVE IS THE THING NOTHING COULD REACH.**
//
// This is the surface the owner's report is about. It renders one of `reportSlotState`'s five
// answers, and for the whole life of that law it lived inside `MyRoomPage` — a component with
// thirty hooks — so every check on it was a REGEX over App.tsx. CE-1's fixture could RUN the
// decision and could never see the tree it produced, which is exactly the gap that let a chip say
// "Ready to open" over an empty slot for 296 members, and let it say nothing at all over 226
// members holding a birth chart with no words in it.
//
// **PROPS IN, TREE OUT, NO HOOK**, which is the entry requirement every extraction on roadmap 7 has
// had: `scripts/r7-components.mjs` and `r8-leaves.mjs` CALL it and read the tree back. The four
// handlers arrive as props rather than being reached for, so the component decides nothing about
// what a tap DOES — it decides only what she is looking at, which is the half that was wrong.
//
// **IT COMPOSES NO SENTENCE OF ITS OWN.** Every word is `t(language, …)` off the catalog, because
// ITEM 14b's census reads the surfaces and a literal typed here is a line no Spanish member ever
// sees translated (R211's finding one directory over).
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";
import { shelfActivityAt } from "../lib/shelf-order";
import { slotClaimsReading, type ReportSlotState, type RoomReportSlot } from "../lib/room-reports";
import { PreviousDetailsBadge } from "./PreviousDetailsBadge";
import { UnwrittenBadge } from "./UnwrittenBadge";
import { RenameControl } from "./RenameControl";
import { DeleteControl } from "./DeleteControl";

export type ChipReport = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt?: string | null;
  activityAt?: string | null;
  fromPreviousDetails?: boolean;
  interpreted?: boolean | null;
};

export function FoundationalReportChip({
  slot, found, slotState, firstTap, generating, arrival, language = DEFAULT_LANGUAGE,
  onOpen, onGenerate, onRename, onDelete,
}: {
  slot: RoomReportSlot;
  found: ChipReport | null;
  slotState: ReportSlotState;
  firstTap: boolean;
  generating: boolean;
  arrival?: boolean;
  language?: string;
  onOpen: (id: string) => void;
  onGenerate: (type: string) => void;
  // R38/R39: both controls read the ANSWER rather than a status code, so both handlers are async by
  // contract and their return types are the controls' own. Typing these as `void` would make the
  // chip silently swallow a refusal — which is the exact class R39 exists for.
  onRename: (id: string, title: string) => Promise<string | null>;
  onDelete: (id: string) => Promise<boolean>;
}) {
  // N18-2: on her FIRST arrival the two charts written for her at signup wear the pointer's own
  // glow, so the note above the panel and the things it is talking about are unmistakably the same
  // thing.
  if (found && slotClaimsReading(slotState)) {
    return (
      <div className={arrival ? "report-chip report-chip--arrival" : "report-chip"}>
        <button type="button" className="report-chip__open" onClick={() => onOpen(found.id)}>
          <span className="report-chip__kind">{slot.label}</span>
          <span className="report-chip__title">{found.title}</span>
          <PreviousDetailsBadge when={found.fromPreviousDetails} />
          <span className="report-chip__date">{new Date(shelfActivityAt(found as never)).toLocaleDateString()}</span>
        </button>
        <RenameControl kind="reading" value={found.title} onRename={(title) => onRename(found.id, title)} className="report-chip__rename-ask" />
        <DeleteControl kind="reading" what={found.title} className="report-chip__delete-ask" onDelete={() => onDelete(found.id)} />
      </div>
    );
  }

  // NIKA: the row is here, her placements and her wheel are real, and the WRITING never arrived. She
  // keeps the door — throwing the chart away would take a real cast off her shelf — and the card
  // says so plainly and offers the one thing that fixes it. It deliberately does NOT wear
  // `report-chip--ready`, which is CE-1's law: only `open` may look ready.
  if (found && slotState === "unwritten") {
    return (
      <div className="report-chip report-chip--unwritten">
        <button type="button" className="report-chip__open" onClick={() => onOpen(found.id)}>
          <span className="report-chip__kind">{slot.label}</span>
          <span className="report-chip__title">{found.title}</span>
          {/* R34 caught this branch in the round that wrote it, correctly: a list that shows a
              reading must ALSO say whether it was made from her old birth details, and the two
              marks are independent facts (R18) — a chart can be stale AND unwritten. */}
          <PreviousDetailsBadge when={found.fromPreviousDetails} />
          <UnwrittenBadge interpreted={found.interpreted} />
        </button>
        <button
          type="button"
          className={generating ? "report-chip__gen is-generating" : "report-chip__gen"}
          onClick={() => onGenerate(slot.type)}
          disabled={generating}
        >
          {generating ? t(language, "reports.writingYours") : t(language, "reports.writeMine")}
        </button>
      </div>
    );
  }

  // N18-1: a missing slot is one of two different things, and the card must not lie about which.
  // CE-1: reaching here means the law did NOT say the row is there, so nothing below may say the
  // reading is ready.
  return (
    <div className="report-chip report-chip--pending">
      <span className="report-chip__kind">{slot.label}</span>
      {slotState === "building" ? (
        <span className="report-chip__date">{t(language, "reports.preparing")}</span>
      ) : (
        <>
          <button
            type="button"
            className={generating ? "report-chip__gen is-generating" : "report-chip__gen"}
            onClick={() => onGenerate(slot.type)}
            disabled={generating}
          >
            {generating ? t(language, "reports.writingYours") : t(language, firstTap ? "reports.writeMine" : "reports.writeItNow")}
          </button>
          <span className="report-chip__date">
            {generating
              ? t(language, "reports.notTemplated")
              : t(language, firstTap ? "reports.freeOnTap" : "reports.didNotArrive")}
          </span>
        </>
      )}
    </div>
  );
}
